import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import FindOneTimeSheetLog from "@/v2/api/time-sheet/FindOneTimeSheetLog";
import AttendanceTable from "@/v2/components/pages/time-sheet/AttendanceTable";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import ViewLayout from "@/v2/layout/View";
import { useQuery } from "@tanstack/react-query";
import {
    BriefcaseBusinessIcon,
    Building2,
    HardHatIcon,
    Pencil,
    ScrollTextIcon,
    Users,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";

const NavButtons = ({ timeSheetId }: { timeSheetId: string }) => {
    return (
        <Link to={`/time-sheet/logs/${timeSheetId}`}>
            <Button size={"sm"} variant={"outline"}>
                <Pencil className="size-4" />
            </Button>
        </Link>
    );
};

export default function ViewOneTimeSheet() {
    const { timeSheetId } = useParams();

    const query = useQuery({
        queryKey: ["time-sheets", timeSheetId],
        queryFn: () =>
            FindOneTimeSheetLog({
                timeSheetId: timeSheetId || "",
            }),
        enabled: !!timeSheetId,
    });

    if (query.isLoading || query.isFetching) {
        return <LoadingAnimation />;
    }

    if (!query.data) return <p>Something went wrong</p>;

    const timeSheetData = query?.data;

    return (
        <ViewLayout
            title="Time Log"
            description="Employees attendance for the respective timelog"
            icon={HardHatIcon}
            buttons={<NavButtons timeSheetId={timeSheetData?.details?.id} />}
        >
            <CardContent className="space-y-8">
                <div>
                    <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold">
                        <ScrollTextIcon className="h-5 w-5" />
                        Time Sheet Details
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <BriefcaseBusinessIcon className="mr-2 h-4 w-4" />
                                Project Name
                            </div>
                            <p className="text-base font-medium">
                                {timeSheetData?.details.project.name || "N/A"}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Building2 className="mr-2 h-4 w-4" />
                                Client Name
                            </div>
                            <p className="text-base font-medium">
                                {timeSheetData?.details.client?.full_name ||
                                    "N/A"}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Building2 className="mr-2 h-4 w-4" />
                                Supplier Name
                            </div>
                            <p className="text-base font-medium">
                                {timeSheetData?.details?.supplier?.full_name ||
                                    "N/A"}
                            </p>
                        </div>
                    </div>
                </div>
                {/* Attendance Table */}
                <div>
                    <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold">
                        <Users className="h-5 w-5" />
                        Employee Attendance
                    </h2>
                    <AttendanceTable
                        attendanceDetails={timeSheetData?.logs}
                        startDate={timeSheetData.details.start_date}
                        endDate={timeSheetData.details?.end_date}
                    />
                </div>
            </CardContent>
        </ViewLayout>
    );
}
